import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { fieldControlClassName } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

const LEGACY_FIELD_CLASS = /(?:^|\s)(?:cm-input|fin-input|um-role-select|rg-input)(?:\s|$)/;
const LOGIN_FIELD_CLASS = /(?:^|\s)ln-input(?:\s|$)/;
const AUTH_FIELD_CLASS = /(?:^|\s)auth-field-control(?:\s|$)/;
const SHADCN_FIELD_CLASS = /\bh-\d/;

export type SearchableSelectOption = {
  value: string;
  label: string;
  searchText?: string;
};

export type SearchableSelectProps = {
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  noOptionsMessage?: string;
  className?: string;
  id?: string;
  menuPortal?: boolean;
  'aria-label'?: string;
  renderOption?: (option: SearchableSelectOption, selected: boolean) => React.ReactNode;
  renderValue?: (option: SearchableSelectOption | undefined) => React.ReactNode;
  filterOption?: (option: SearchableSelectOption, query: string) => boolean;
  /** Shows a clear control when a value is selected so it can be unset. */
  clearable?: boolean;
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  error = false,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No options available.',
  noOptionsMessage = 'No options match your search.',
  className = '',
  id,
  menuPortal = true,
  'aria-label': ariaLabel,
  renderOption,
  renderValue,
  filterOption,
  clearable = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const detachListWheelRef = useRef<(() => void) | null>(null);

  const bindListRef = useCallback((node: HTMLDivElement | null) => {
    detachListWheelRef.current?.();
    detachListWheelRef.current = null;
    if (!node) return;

    const onWheel = (event: WheelEvent) => {
      const maxScroll = node.scrollHeight - node.clientHeight;
      if (maxScroll > 0) {
        node.scrollTop = Math.min(maxScroll, Math.max(0, node.scrollTop + event.deltaY));
      }
      event.preventDefault();
      event.stopPropagation();
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    detachListWheelRef.current = () => node.removeEventListener('wheel', onWheel);
  }, []);

  const optionsWithValue = useMemo(() => {
    if (value && !options.some((option) => option.value === value)) {
      return [{ value, label: value }, ...options];
    }
    return options;
  }, [options, value]);

  const selected = useMemo(
    () => optionsWithValue.find((option) => option.value === value),
    [optionsWithValue, value],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return optionsWithValue;
    return optionsWithValue.filter((option) => {
      if (filterOption) return filterOption(option, query);
      const haystack = `${option.label} ${option.searchText ?? ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [optionsWithValue, search, filterOption]);

  const showClear = clearable && Boolean(value) && !disabled;

  const usesToolbarSelectStyle = /\btm-toolbar-select\b/.test(className);
  const usesLegacyFieldStyle = LEGACY_FIELD_CLASS.test(className);
  const usesAuthFieldStyle = AUTH_FIELD_CLASS.test(className);
  const usesLoginFieldStyle = LOGIN_FIELD_CLASS.test(className);
  const usesShadcnFieldStyle =
    !usesToolbarSelectStyle &&
    !usesLegacyFieldStyle &&
    !usesAuthFieldStyle &&
    !usesLoginFieldStyle &&
    (SHADCN_FIELD_CLASS.test(className) || className.includes('rounded-md'));

  const triggerClassName = cn(
    'flex w-full whitespace-nowrap text-left text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
    usesToolbarSelectStyle
      ? [
          'tm-toolbar-select relative min-h-0 items-center justify-start gap-0',
          showClear ? 'pr-12' : 'pr-8',
          className.replace(/\bh-\d+\b/g, '').replace(/\bw-full\b/g, '').trim(),
          error && 'border-destructive',
        ]
      : [
          'items-center justify-between gap-2',
          usesLoginFieldStyle
            ? [className, error && 'err']
            : usesAuthFieldStyle
              ? [
                  'h-auto min-h-0 rounded-[inherit] border-0 bg-transparent px-[inherit] py-[inherit] shadow-none focus:ring-0',
                  className,
                ]
              : usesLegacyFieldStyle
                ? [
                    'h-auto min-h-0 rounded-[inherit] border-0 bg-transparent px-[inherit] py-[inherit] shadow-none focus:ring-0',
                    className,
                    error && 'err',
                  ]
                : usesShadcnFieldStyle
                  ? [fieldControlClassName, error && 'border-destructive', className]
                  : [
                      'cm-input h-auto min-h-0 rounded-[inherit] border-0 bg-transparent px-[inherit] py-[inherit] shadow-none focus:ring-0',
                      error && 'err',
                      className,
                    ],
        ],
    open &&
      !usesToolbarSelectStyle &&
      !usesShadcnFieldStyle &&
      !usesAuthFieldStyle &&
      !usesLoginFieldStyle &&
      'open',
    open && (usesShadcnFieldStyle || usesAuthFieldStyle) && 'ring-1 ring-ring',
  );

  useEffect(() => {
    if (!open) return;
    // On touch phones, autofocusing opens the keyboard and can hide the menu behind it.
    // Desktop keeps autofocus; mobile users tap search when they need the keyboard.
    const isTouchUi =
      typeof window !== 'undefined' &&
      window.matchMedia('(pointer: coarse)').matches;
    if (isTouchUi) return;

    const frame = window.requestAnimationFrame(() => {
      searchRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  // Mobile/touch: Radix non-modal popovers often miss outside taps on non-interactive
  // areas. Close explicitly when the pointer lands outside trigger + menu.
  useEffect(() => {
    if (!open) return;

    const handleOutsidePointer = (event: PointerEvent | TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      if (contentRef.current?.contains(target)) return;
      setOpen(false);
      setSearch('');
    };

    document.addEventListener('pointerdown', handleOutsidePointer, true);
    document.addEventListener('touchstart', handleOutsidePointer, true);
    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointer, true);
      document.removeEventListener('touchstart', handleOutsidePointer, true);
    };
  }, [open]);

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange('');
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={rootRef} className={cn('relative min-w-0', usesToolbarSelectStyle ? 'w-full max-w-full' : 'w-full')}>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setSearch('');
        }}
        modal={false}
      >
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            aria-label={ariaLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={triggerClassName}
          >
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-left',
                showClear && !usesToolbarSelectStyle && 'pr-5',
                selected ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {renderValue ? renderValue(selected) : selected?.label ?? placeholder}
            </span>
            <ChevronDown
              className={cn(
                'shrink-0 opacity-50',
                usesToolbarSelectStyle ? 'tm-toolbar-select-chevron h-3.5 w-3.5' : 'h-4 w-4',
              )}
            />
          </button>
        </PopoverTrigger>
        {showClear && (
          <button
            type="button"
            aria-label="Clear selection"
            className={cn(
              'absolute top-1/2 z-[1] flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground',
              usesToolbarSelectStyle ? 'right-6' : 'right-8',
            )}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={handleClear}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}

        <PopoverContent
          ref={contentRef}
          portal={menuPortal}
          align="start"
          collisionPadding={8}
          className="z-[100] flex w-[var(--radix-popover-trigger-width)] max-h-[min(15rem,var(--radix-popover-content-available-height))] flex-col overflow-hidden p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onWheel={(event) => event.stopPropagation()}
        >
          <div className="border-b border-border bg-popover p-2">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
              onKeyDown={(event) => event.stopPropagation()}
            />
          </div>

          {optionsWithValue.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">{emptyMessage}</p>
          ) : filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">{noOptionsMessage}</p>
          ) : (
            <div
              ref={bindListRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
              role="listbox"
            >
              {filtered.map((option) => {
                const isSelected = value === option.value;
                return (
                  <button
                    key={option.value || '__empty__'}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={cn(
                      'relative flex w-full cursor-pointer items-center rounded-sm py-1.5 pl-2 pr-8 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-accent/60',
                    )}
                    onClick={() => handleSelect(option.value)}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {renderOption ? renderOption(option, isSelected) : option.label}
                    </span>
                    {isSelected && (
                      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SearchableSelect;
